/**
 * Password strength scoring for the sign-up form.
 *
 * Rolled by hand rather than reaching for `zxcvbn` — we don't need
 * dictionary-attack analysis or full-blown entropy estimation for a
 * sign-up flow whose minimum bar already enforces 12 chars + four
 * character classes. A simple 0–4 progress score that maps to the
 * shadcn-styled meter is enough to give users actionable feedback
 * without paying for ~140 KB of library.
 *
 * The score maps to four meter segments:
 *   0 — empty                    (no bars filled)
 *   1 — too weak                 (1 bar, destructive)
 *   2 — getting there            (2 bars, destructive)
 *   3 — adequate                 (3 bars, warning)
 *   4 — strong                   (4 bars, success)
 */

export interface PasswordRequirements {
  readonly minLength: number;
  readonly requireLowercase: boolean;
  readonly requireUppercase: boolean;
  readonly requireNumbers: boolean;
  readonly requireSymbols: boolean;
}

/**
 * Mirrors the Cognito user pool's `password_policy` in
 * infrastructure/envs/staging/cognito.tf.
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirements = {
  minLength: 12,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSymbols: true,
};

export interface PasswordAssessment {
  /** 0–4, see file header. */
  readonly score: 0 | 1 | 2 | 3 | 4;
  /** Short label suitable for inline display next to the field. */
  readonly label: 'empty' | 'too-weak' | 'weak' | 'fair' | 'strong';
  /** Whether the password meets every rule in PASSWORD_REQUIREMENTS. */
  readonly meetsRequirements: boolean;
  /** Each rule's individual met/not-met state, for the requirements list. */
  readonly checks: {
    readonly minLength: boolean;
    readonly lowercase: boolean;
    readonly uppercase: boolean;
    readonly numbers: boolean;
    readonly symbols: boolean;
  };
}

const LOWERCASE_RE = /[a-z]/;
const UPPERCASE_RE = /[A-Z]/;
const NUMBER_RE = /[0-9]/;
// Symbols per Cognito: any non-alphanumeric. Cognito's accepted set is broad
// (RFC-defined punctuation + space); for assessment purposes we accept the
// same.
const SYMBOL_RE = /[^A-Za-z0-9]/;

export function assessPassword(password: string): PasswordAssessment {
  const checks = {
    minLength: password.length >= PASSWORD_REQUIREMENTS.minLength,
    lowercase: LOWERCASE_RE.test(password),
    uppercase: UPPERCASE_RE.test(password),
    numbers: NUMBER_RE.test(password),
    symbols: SYMBOL_RE.test(password),
  };

  if (password.length === 0) {
    return {
      score: 0,
      label: 'empty',
      meetsRequirements: false,
      checks,
    };
  }

  const charClassesPresent =
    Number(checks.lowercase) +
    Number(checks.uppercase) +
    Number(checks.numbers) +
    Number(checks.symbols);

  const meetsRequirements =
    checks.minLength && checks.lowercase && checks.uppercase && checks.numbers && checks.symbols;

  if (meetsRequirements) {
    return { score: 4, label: 'strong', meetsRequirements: true, checks };
  }

  // Below requirements: derive 1-3 from length + character-class variety.
  // Tight rules so the UX guides the user toward "strong" not "good enough".
  if (password.length < 6 || charClassesPresent < 2) {
    return { score: 1, label: 'too-weak', meetsRequirements: false, checks };
  }
  if (password.length < 10 || charClassesPresent < 3) {
    return { score: 2, label: 'weak', meetsRequirements: false, checks };
  }
  return { score: 3, label: 'fair', meetsRequirements: false, checks };
}
